/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
import React, { useState, useRef } from 'react';
import { useUIStore } from './store/useUIStore';
import { SplashScreen } from './components/Coordinate/SplashScreen';
import { Dashboard } from './components/Dashboard/Dashboard';
import { COAUForm } from './components/COAU/COAUForm';
import { CoordinateManager } from './components/Coordinate/CoordinateManager';
import { Fraseologia } from './components/Fraseologia/Fraseologia';
import MediaTab from './components/Media/MediaTab';
import './components/Media/MediaTab.css';
import { RisorseManager } from './components/Risorse/RisorseManager';
import { SITAC } from './components/SITAC/SITAC';
import { LACES } from './components/LACES/LACES';
import { useAppStore } from './store/useAppStore';
import { DiarioOperativo } from './components/Diario/DiarioOperativo';
import { MeteoFWI } from './components/Meteo/MeteoFWI';
import { decodificaPayloadDaURL, importaPayloadQR } from './components/QR/QRCambioDOS';
import BriefingPrePartenza from './components/Briefing/BriefingPrePartenza';
import 'leaflet/dist/leaflet.css';
import './index.css';

type ViewState = 
  | 'splash' 
  | 'dashboard' 
  | 'coau' 
  | 'coordinate' 
  | 'risorse' 
  | 'laces' 
  | 'sitac' 
  | 'fraseologia' 
  | 'media' 
  | 'cambio-dos'
  | 'diario'
  | 'meteo';

const App: React.FC = () => {
  const [view, setView] = useState<ViewState>('splash');
  const [qrImportato, setQrImportato] = React.useState(false);
  const [showBriefing, setShowBriefing] = React.useState(false);
  const [mapCenter, setMapCenter] = React.useState<[number, number] | null>(null);
  const { navVisible, setNavVisible, openAllMenus, closeAllMenus } = useUIStore();
  const navTouchStartY = useRef(0);

  const onNavTouchStart = (e: React.TouchEvent) => {
    navTouchStartY.current = e.touches[0].clientY;
  };
  const onNavTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientY - navTouchStartY.current;
    if (delta < -35) closeAllMenus();
  };
  const onHandleTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientY - navTouchStartY.current;
    if (delta > 35) openAllMenus();
  };

  // Rileva QR Code ?d518= nell'URL — import automatico al lancio
  React.useEffect(() => {
    const payload = decodificaPayloadDaURL();
    if (payload && !qrImportato) {
      importaPayloadQR(payload);
      setQrImportato(true);
      // Vai direttamente alla dashboard dopo import
      setView('dashboard');
    }
  }, []);

  // Splash Screen iniziale
  if (view === 'splash' && !qrImportato) {
    return <SplashScreen onDismiss={() => { setView('dashboard'); setShowBriefing(true); }} />;
  }

  return (
    <div className="app-main-layout" style={{ backgroundColor: '#000', minHeight: '100vh' }}>
      
      {/* HEADER - Solo nella Dashboard */}
      {view === 'dashboard' && (
        <div className="header-generale-arancio" style={{
          backgroundColor: '#ff6b35',
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '10px',
          borderRadius: '12px',
          boxShadow: '0 4px 10px rgba(0,0,0,0.5)'
        }}>
          <h1 style={{ 
            color: '#fff', 
            margin: 0, 
            fontSize: '2.5rem', 
            fontWeight: '900',
            letterSpacing: '1px',
            textTransform: 'uppercase'
          }}>
            🔥 <span style={{color: '#000'}}>D</span>elta <span style={{color: '#000'}}>O</span>scar <span style={{color: '#000'}}>S</span>ierra <span style={{color: '#000'}}>5 1 8</span>
          </h1>
        </div>
      )}

      {/* NAVIGAZIONE */}
      <div style={{
        overflow: 'hidden',
        maxHeight: navVisible ? '120px' : '0',
        transition: 'max-height 0.3s ease',
      }}>
        <nav
          onTouchStart={onNavTouchStart}
          onTouchEnd={onNavTouchEnd}
          style={{
            display: 'flex',
            gap: '8px',
            padding: '15px 10px',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            scrollbarWidth: 'none',
            alignItems: 'center',
          }}
        >
          <button onClick={() => setView('dashboard')} className={`nav-btn ${view === 'dashboard' ? 'active' : ''}`}>📊 Dashboard</button>
          <button
            onClick={() => setShowBriefing(true)}
            className="nav-btn"
            style={{ borderColor: '#f97316', color: '#f97316', fontWeight: 700 }}
            title="Briefing pre-partenza — inserisci dati intervento"
          >🚒 Briefing</button>
          <button onClick={() => setView('coau')} className={`nav-btn ${view === 'coau' ? 'active' : ''}`}>📄 Scheda COAU</button>
          <button onClick={() => setView('coordinate')} className={`nav-btn ${view === 'coordinate' ? 'active' : ''}`}>📍 Coordinate</button>
          <button onClick={() => setView('risorse')} className={`nav-btn ${view === 'risorse' ? 'active' : ''}`}>👥 Risorse</button>
          <button onClick={() => setView('fraseologia')} className={`nav-btn ${view === 'fraseologia' ? 'active' : ''}`}>📢 Fraseologia</button>
          <button onClick={() => setView('sitac')} className={`nav-btn ${view === 'sitac' ? 'active' : ''}`}>🗺️ SI.TA.C.</button>
          <button onClick={() => setView('laces')} className={`nav-btn ${view === 'laces' ? 'active' : ''}`}>🛡️ L.A.C.E.S.</button>
          <button onClick={() => setView('media')} className={`nav-btn ${view === 'media' ? 'active' : ''}`}>🖼️ Media</button>
          <button onClick={() => setView('diario')} className={`nav-btn ${view === 'diario' ? 'active' : ''}`} style={view === 'diario' ? {} : { borderColor: '#6366f1', color: '#a5b4fc' }}>📓 Diario</button>
          <button onClick={() => setView('meteo')} className={`nav-btn ${view === 'meteo' ? 'active' : ''}`} style={view === 'meteo' ? {} : { borderColor: '#0891b2', color: '#67e8f9' }}>🌡️ Meteo/FWI</button>
          {/* Bottone chiudi tutti i menu — fisso a destra */}
          <button
            onClick={closeAllMenus}
            style={{
              marginLeft: 'auto', flexShrink: 0,
              padding: '8px 14px', borderRadius: 6,
              border: '1px solid rgba(100,116,139,0.5)',
              background: 'rgba(30,41,59,0.8)',
              color: '#94a3b8', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            title="Chiudi tutti i menu"
          >✕ Chiudi</button>
        </nav>
      </div>

      {/* Striscia apri menu — visibile solo quando menu è nascosto */}
      {!navVisible && (
        <div
          onTouchStart={(e) => { navTouchStartY.current = e.touches[0].clientY; }}
          onTouchEnd={onHandleTouchEnd}
          onClick={openAllMenus}
          style={{
            height: 48,
            background: 'rgba(15,23,42,0.95)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
          }}
        >
          <span style={{ color: '#94a3b8', fontSize: 15, fontWeight: 600, userSelect: 'none', letterSpacing: 1 }}>
            ☰ Apri menu
          </span>
        </div>
      )}

      {/* CONTENUTO PRINCIPALE */}
      <main style={{ padding: view === 'sitac' ? '0' : '0 10px' }}>
        {view === 'dashboard' && <Dashboard onNavigate={(target: any) => setView(target)} />}
        {view === 'coau' && <COAUForm onOpenMap={() => setView('sitac')} />}
        {view === 'coordinate' && <CoordinateManager />}
        {view === 'risorse' && <RisorseManager />}
        {view === 'fraseologia' && <Fraseologia />}
        {view === 'sitac' && <SITAC />}
        {view === 'media' && <MediaSection />}
        {view === 'laces' && <LACES />}

        {/* Diario Operativo — include Cambio DOS */}
        {view === 'diario' && <DiarioOperativo />}
        {view === 'meteo' && <MeteoFWI />}
        {view === 'cambio-dos' && <DiarioOperativo />}
      </main>

      {/* FOOTER — nascosto in SITAC per massimizzare la mappa */}
      {view !== 'sitac' && (
        <footer style={{
          padding: '20px',
          textAlign: 'center',
          color: '#444',
          fontSize: '0.8rem',
          letterSpacing: '1px'
        }}>
          SISTEMA DOS518 | IRTILAC S.r.l. - WEB VERSION 2025
        </footer>
      )}

      {/* MODALE BRIEFING PRE-PARTENZA */}
      {showBriefing && (
        <BriefingPrePartenza
          onClose={() => setShowBriefing(false)}
          onAvvia={(lat, lon) => {
            setMapCenter([lat, lon]);
            setView('sitac');
          }}
        />
      )}
    </div>
  );
};

// Componente wrapper per MediaTab che si collega allo store
const MediaSection: React.FC = () => {
  const { media, addMedia, deleteMedia } = useAppStore();

  return (
    <MediaTab
      mediaItems={media as any}
      onAddMedia={addMedia as any}
      onDeleteMedia={deleteMedia}
    />
  );
};

export default App;
